import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress,
  useTheme,
  Stack,
  TextField
} from '@mui/material';
import { Google as GoogleIcon, AccountCircle as MicrosoftIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { signInWithGoogle, signInWithMicrosoft, signInWithEmailPassword } from '../../firebase/auth';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

// Dev-mode email/password login — only renders on localhost so the form
// never ships to production users. Used by QA sweeps that can't do OAuth
// in debug Chrome. See memory/qa-sweep-methodology.md.
const IS_LOCAL_DEV =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const LoginForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [providerHint, setProviderHint] = useState('');
  // Local-only dev credentials state
  const [devEmail, setDevEmail] = useState('');
  const [devPassword, setDevPassword] = useState('');

  // Redirect to dashboard when authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const isProviderMismatch = (err) => {
    const s = String(err || '').toLowerCase();
    return (
      s.includes('account-exists-with-different-credential') ||
      s.includes('user-not-found') ||
      s.includes('email-already-in-use')
    );
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setAuthError('');
    setProviderHint('');
    const { error } = await signInWithGoogle();
    if (error && error !== 'redirect_initiated') {
      if (isProviderMismatch(error)) {
        setProviderHint('Your email may use Microsoft instead. Try the Microsoft button below.');
      } else {
        setAuthError(error);
      }
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    setAuthError('');
    setProviderHint('');
    const { error } = await signInWithMicrosoft();
    if (error && error !== 'redirect_initiated') {
      if (isProviderMismatch(error)) {
        setProviderHint('Your email may use Google instead. Try the Google button above.');
      } else {
        setAuthError(error);
      }
      setLoading(false);
    }
  };

  const handleDevEmailLogin = async (e) => {
    e.preventDefault();
    if (!devEmail || !devPassword) {
      setAuthError('Email and password are required');
      return;
    }
    setLoading(true);
    setAuthError('');
    const { error } = await signInWithEmailPassword(devEmail, devPassword);
    if (error) {
      setAuthError(error);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 4, md: 6 },
              ...theme.customEffects?.glass,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 5,
              textAlign: 'center'
            }}
          >
            <Box sx={{ mb: 4 }}>
              <img 
                src={logo} 
                alt="FutureSense"
                style={{ height: '48px', width: 'auto', marginBottom: '24px' }}
              />
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 800,
                  color: '#ffffff',
                  mb: 1,
                  letterSpacing: '-0.03em'
                }}
              >
                Welcome back.
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                Sign in to your practitioner dashboard.
              </Typography>
            </Box>
        
            {authError && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {authError}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3, fontSize: '0.75rem' }}>
              Sign in with your business email. Most work with Google — try it first.
            </Alert>

            {providerHint && (
              <Alert severity="warning" sx={{ mb: 3, fontSize: '0.75rem' }}>
                {providerHint}
              </Alert>
            )}

            <Stack spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={loading}
                startIcon={<GoogleIcon />}
                size="large"
                sx={{ 
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.03)'
                  }
                }}
              >
                Continue with Google
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={handleMicrosoftSignIn}
                disabled={loading}
                startIcon={<MicrosoftIcon />}
                size="large"
                sx={{ 
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.03)'
                  }
                }}
              >
                Continue with Microsoft
              </Button>
            </Stack>

            {IS_LOCAL_DEV && (
              <>
                <Divider sx={{ my: 3, borderColor: 'rgba(255, 165, 0, 0.3)' }}>
                  <Typography variant="caption" sx={{ color: '#ffa500', px: 1, fontWeight: 700 }}>
                    LOCALHOST DEV LOGIN
                  </Typography>
                </Divider>
                <Box
                  component="form"
                  onSubmit={handleDevEmailLogin}
                  sx={{ textAlign: 'left' }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    label="Email"
                    value={devEmail}
                    onChange={(e) => setDevEmail(e.target.value)}
                    autoComplete="email"
                    sx={{ mb: 1.5, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Password"
                    value={devPassword}
                    onChange={(e) => setDevPassword(e.target.value)}
                    autoComplete="current-password"
                    sx={{ mb: 2, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ background: '#ffa500', color: '#000', fontWeight: 700, '&:hover': { background: '#ff9500' } }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Dev Login'}
                  </Button>
                </Box>
              </>
            )}

            <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.08)' }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, px: 1 }}>
                SECURE AUTHENTICATION
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Don't have an account?{' '}
                <Link to="/signup" style={{ textDecoration: 'none', color: theme.palette.primary.light, fontWeight: 600 }}>
                  Join Free
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LoginForm;